import React, { useEffect, useState } from "react";
import axios from "axios";
import { Boxes, PlusCircle, X, Trash2, ChevronDown, ChevronRight, Edit, Trash2 as DeleteIcon, AlertTriangle, Filter, Heading1 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getApiUrl, getAuthHeaders } from "../config/api";

// Utility function to format date without timezone conversion
const formatDate = (dateString) => {
  if (!dateString) return 'No date';
  // Split the date string and format it directly to avoid timezone issues
  const [year, month, day] = dateString.split('T')[0].split('-');
  return `${month}/${day}/${year}`;
};

export default function Supplies() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState({ from: "", to: "", vendor: "" });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingOrder, setDeletingOrder] = useState(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const [form, setForm] = useState({
    vendorName: "",
    orderDate: new Date().toISOString().slice(0, 10),
    shipping: "0",
    other_total: "0",
    tax_total: "0",
    notes: "",
    lines: [
      {
        productName: "",
        brandName: "",
        categoryName: "",
        qty: "1",
        unit_desc: "",
        unit_price: "0",
        tax_rate: "0",
        other_line_amt: "0",
        notes: ""
      }
    ]
  });
  const [vendorOptions, setVendorOptions] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setIsLoading(true);
    setError("");
    try {
      const params = {};
      if (filters.from) params.from = filters.from;
      if (filters.to) params.to = filters.to;
      if (filters.vendor) params.vendor = filters.vendor;
      const res = await axios.get(getApiUrl("/api/supplies/orders"), {
        headers: getAuthHeaders(),
        params
      });
      setOrders(res.data || []);
    } catch (e) {
      setError(e.response?.data?.error || "Failed to load orders");
    } finally {
      setIsLoading(false);
    }
  };

  const toggleOrderExpansion = (orderId) => {
    setExpandedOrders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const confirmDelete = (order, e) => {
    e.stopPropagation(); // Prevent row click
    setDeletingOrder(order);
    setShowDeleteConfirm(true);
  };

  const deleteOrder = async () => {
    if (!deletingOrder) return;
    try {
      await axios.delete(getApiUrl(`/api/supplies/orders/${deletingOrder.id}`), { headers: getAuthHeaders() });
      setOrders(prev => prev.filter(order => order.id !== deletingOrder.id));
      setShowDeleteConfirm(false);
      setDeletingOrder(null);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to delete order');
    }
  };

  const openModal = () => {
    setError("");
    setForm((prev) => ({ ...prev }));
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const updateHeader = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const updateLine = (idx, name, value) => {
    setForm((prev) => {
      const lines = [...prev.lines];
      lines[idx] = { ...lines[idx], [name]: value };
      return { ...prev, lines };
    });
  };

  const fetchVendors = async (q) => {
    try {
      const res = await axios.get(getApiUrl('/api/supplies/lookups/vendors'), { headers: getAuthHeaders(), params: { q } });
      setVendorOptions(res.data || []);
    } catch {}
  };

  const fetchBrands = async (q) => {
    try {
      const res = await axios.get(getApiUrl('/api/supplies/lookups/brands'), { headers: getAuthHeaders(), params: { q } });
      setBrandOptions(res.data || []);
    } catch {}
  };

  const fetchCategories = async (q) => {
    try {
      const res = await axios.get(getApiUrl('/api/supplies/lookups/categories'), { headers: getAuthHeaders(), params: { q } });
      setCategoryOptions(res.data || []);
    } catch {}
  };

  const addLine = () => {
    setForm((prev) => ({
      ...prev,
      lines: [
        ...prev.lines,
        {
          productName: "",
          brandName: "",
          categoryName: "",
          qty: "1",
          unit_desc: "",
          unit_price: "0",
          tax_rate: "0",
          other_line_amt: "0",
          notes: ""
        }
      ]
    }));
  };

  const removeLine = (idx) => {
    setForm((prev) => ({
      ...prev,
      lines: prev.lines.filter((_, i) => i !== idx)
    }));
  };

  const saveOrder = async () => {
    setSaving(true);
    setError("");
    try {
      await axios.post(getApiUrl("/api/supplies/orders"), { ...form, currency: "USD" }, {
        headers: getAuthHeaders()
      });
      setIsModalOpen(false);
      // Reset minimal fields for next time
      setForm((prev) => ({
        ...prev,
        vendorName: "",
        notes: "",
        lines: [
          {
            productName: "",
            brandName: "",
            categoryName: "",
            qty: "1",
            unit_desc: "",
            unit_price: "0",
            tax_rate: "0",
            other_line_amt: "0",
            notes: ""
          }
        ]
      }));
      fetchOrders();
    } catch (e) {
      setError(e.response?.data?.error || "Failed to save order");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="p-3 sm:p-6 overflow-x-hidden">
        {/* Mobile Header */}
        <div className="sm:hidden mb-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="flex items-center text-lg font-semibold text-gray-800">
              <Boxes className="mr-2 text-black-500 w-5 h-5" /> Supplies
            </h2>
            <button onClick={openModal} className="inline-flex items-center px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm">
              <PlusCircle className="mr-1 w-4 h-4" /> New
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button 
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              className="inline-flex justify-center items-center px-2 py-2 border rounded-md hover:bg-gray-50 text-xs"
            >
              <Filter className="mr-1 w-3 h-3" /> Filters
            </button>
            <button onClick={() => navigate('/supplies/reports')} className="inline-flex justify-center items-center px-2 py-2 border rounded-md hover:bg-gray-50 text-xs">Reports</button>
          </div>
        </div>

        {/* Desktop Header */}
        <div className="hidden sm:flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <h1 className="flex items-center text-xl sm:text-2xl font-bold text-gray-800">
            <Boxes className="mr-2 text-black-500" /> Supplies
          </h1>
          <div className="flex items-stretch sm:items-center gap-2 w-full sm:w-auto">
            <button onClick={() => navigate('/supplies/reports')} className="inline-flex justify-center items-center px-3 py-2 border rounded-md hover:bg-gray-50 flex-1 sm:flex-none">Supplies Reports</button>
            <button onClick={() => navigate('/supplies-lookups')} className="inline-flex justify-center items-center px-3 py-2 border rounded-md hover:bg-gray-50 flex-1 sm:flex-none">Manage Lookups</button>
            <button onClick={openModal} className="inline-flex justify-center items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md flex-1 sm:flex-none">
              <PlusCircle className="mr-2" /> New Purchase
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-3 sm:p-4">
          {/* Mobile Filters - Collapsible */}
          <div className="sm:hidden">
            {showMobileFilters && (
              <div className="mb-4 space-y-3">
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">From</label>
                    <input type="date" value={filters.from} onChange={(e) => setFilters(f => ({ ...f, from: e.target.value }))} className="w-full border rounded px-2 py-1 text-xs" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">To</label>
                    <input type="date" value={filters.to} onChange={(e) => setFilters(f => ({ ...f, to: e.target.value }))} className="w-full border rounded px-2 py-1 text-xs" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Vendor</label>
                    <input value={filters.vendor} onChange={(e) => setFilters(f => ({ ...f, vendor: e.target.value }))} placeholder="Search vendor" className="w-full border rounded px-2 py-1 text-xs" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={fetchOrders} className="flex-1 px-3 py-2 border rounded text-xs hover:bg-gray-50">Apply</button>
                  <button onClick={() => { setFilters({ from: "", to: "", vendor: "" }); setTimeout(fetchOrders, 0); }} className="flex-1 px-3 py-2 border rounded text-xs hover:bg-gray-50">Reset</button>
                </div>
              </div>
            )}
          </div>

          {/* Desktop Filters */}
          <div className="hidden sm:block mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">From</label>
              <input type="date" value={filters.from} onChange={(e) => setFilters(f => ({ ...f, from: e.target.value }))} className="w-full border rounded px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm" />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">To</label>
              <input type="date" value={filters.to} onChange={(e) => setFilters(f => ({ ...f, to: e.target.value }))} className="w-full border rounded px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm" />
            </div>
            <div className="sm:col-span-2 lg:col-span-2">
              <label className="block text-xs text-gray-600 mb-1">Vendor</label>
              <input value={filters.vendor} onChange={(e) => setFilters(f => ({ ...f, vendor: e.target.value }))} placeholder="Search vendor" className="w-full border rounded px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm" />
            </div>
            <div className="flex items-stretch sm:items-end gap-2">
              <button onClick={fetchOrders} className="px-2 py-1 sm:px-3 sm:py-2 border rounded text-xs sm:text-sm hover:bg-gray-50 flex-1 sm:flex-none">Apply</button>
              <button onClick={() => { setFilters({ from: "", to: "", vendor: "" }); setTimeout(fetchOrders, 0); }} className="px-2 py-1 sm:px-3 sm:py-2 border rounded text-xs sm:text-sm hover:bg-gray-50 flex-1 sm:flex-none">Reset</button>
            </div>
          </div>
          {error && (
            <div className="mb-3 p-3 rounded bg-red-50 text-red-700 text-sm">{error}</div>
          )}
          {isLoading ? (
            <p className="text-gray-600">Loading...</p>
          ) : orders.length === 0 ? (
            <p className="text-gray-600">Your supply purchases will appear here. Create a new purchase to get started.</p>
          ) : (
            <>
              <div className="sm:hidden space-y-3">
                {orders.map((o) => (
                  <div key={o.id} className="border rounded-lg p-3 shadow-sm">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="text-xs text-gray-500">Date</div>
                        <div className="text-sm font-medium">{formatDate(o.order_date)}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-500">Total</div>
                        <div className="text-sm font-semibold">{Number(o.grand_total).toLocaleString(undefined, { style: 'currency', currency: o.currency || 'USD' })}</div>
                      </div>
                    </div>

                    <div className="mt-1 text-gray-800 text-sm">
                      <span className="text-xs text-gray-500">Vendor</span>
                      <div className="font-medium">{o.vendor_name}</div>
                    </div>

                    <div className="mt-2 grid grid-cols-3 gap-1 sm:gap-2 text-xs">
                      <div>
                        <div className="text-gray-500">Subtotal</div>
                        <div className="font-medium truncate">{Number(o.subtotal).toLocaleString(undefined, { style: 'currency', currency: o.currency || 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                      </div>
                      <div>
                        <div className="text-gray-500">Tax</div>
                        <div className="font-medium truncate">{Number(o.tax_total).toLocaleString(undefined, { style: 'currency', currency: o.currency || 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                      </div>
                      <div>
                        <div className="text-gray-500">Shipping</div>
                        <div className="font-medium truncate">{Number(o.shipping).toLocaleString(undefined, { style: 'currency', currency: o.currency || 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                      </div>
                    </div>

                    {expandedOrders.has(o.id) && o.lines && o.lines.length > 0 && (
                      <div className="mt-2 space-y-2">
                        {o.lines.map((line, idx) => (
                          <div key={idx} className="border rounded p-2">
                            <div className="text-sm font-medium">{line.product_name || 'Unnamed Product'}</div>
                            <div className="text-xs text-gray-500">
                              {(line.brand_name || '-')}{line.category_name ? ` • ${line.category_name}` : ''}
                            </div>
                            <div className="mt-1 flex justify-between text-xs">
                              <div>Qty: {Number(line.qty).toFixed(2)}</div>
                              <div>{Number(line.unit_price).toLocaleString(undefined, { style: 'currency', currency: o.currency || 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })} ea</div>
                              <div className="font-medium">{Number(line.line_total).toLocaleString(undefined, { style: 'currency', currency: o.currency || 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="mt-3 flex justify-between items-center">
                      <button
                        onClick={() => toggleOrderExpansion(o.id)}
                        className="text-gray-600 text-xs sm:text-sm inline-flex items-center"
                        title={expandedOrders.has(o.id) ? 'Hide items' : 'Show items'}
                      >
                        {expandedOrders.has(o.id) ? (
                          <>
                            <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> Hide
                          </>
                        ) : (
                          <>
                            <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> Show
                          </>
                        )}
                      </button>
                      <div className="flex items-center gap-1 sm:gap-2">
                        <button
                          onClick={() => navigate(`/supplies/${o.id}?edit=true`)}
                          className="px-2 py-1 sm:px-3 sm:py-1 border rounded text-xs"
                        >
                          Edit
                        </button>
                        <button
                          onClick={(e) => confirmDelete(o, e)}
                          className="px-2 py-1 sm:px-3 sm:py-1 border rounded text-xs text-red-600"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="hidden sm:block overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-600 border-b">
                      <th className="py-2 pr-2 w-8"></th>
                      <th className="py-2 pr-4">Date</th>
                      <th className="py-2 pr-4">Vendor</th>
                      <th className="py-2 pr-4 text-right">Subtotal</th>
                      <th className="py-2 pr-4 text-right hidden lg:table-cell">Tax</th>
                      <th className="py-2 pr-4 text-right hidden lg:table-cell">Shipping</th>
                      <th className="py-2 pr-4 text-right hidden lg:table-cell">Other</th>
                      <th className="py-2 pr-4 text-right">Total</th>
                      <th className="py-2 pr-2 w-16"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((o) => (
                      <React.Fragment key={o.id}>
                        <tr className="border-b hover:bg-gray-50">
                          <td className="py-2 pr-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleOrderExpansion(o.id);
                              }}
                              className="text-gray-500 hover:text-gray-700 transition-colors"
                              title={expandedOrders.has(o.id) ? "Collapse details" : "Expand details"}
                            >
                              {expandedOrders.has(o.id) ? (
                                <ChevronDown className="w-4 h-4" />
                              ) : (
                                <ChevronRight className="w-4 h-4" />
                              )}
                            </button>
                          </td>
                          <td className="py-2 pr-4 cursor-pointer" onClick={() => navigate(`/supplies/${o.id}`)}>
                            {formatDate(o.order_date)}
                          </td>
                          <td className="py-2 pr-4 cursor-pointer" onClick={() => navigate(`/supplies/${o.id}`)}>
                            {o.vendor_name}
                          </td>
                          <td className="py-2 pr-4 text-right cursor-pointer" onClick={() => navigate(`/supplies/${o.id}`)}>
                            {Number(o.subtotal).toLocaleString(undefined, { style: 'currency', currency: o.currency || 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="py-2 pr-4 text-right hidden lg:table-cell cursor-pointer" onClick={() => navigate(`/supplies/${o.id}`)}>
                            {Number(o.tax_total).toLocaleString(undefined, { style: 'currency', currency: o.currency || 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="py-2 pr-4 text-right hidden lg:table-cell cursor-pointer" onClick={() => navigate(`/supplies/${o.id}`)}>
                            {Number(o.shipping).toLocaleString(undefined, { style: 'currency', currency: o.currency || 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="py-2 pr-4 text-right hidden lg:table-cell cursor-pointer" onClick={() => navigate(`/supplies/${o.id}`)}>
                            {Number(o.other_total).toLocaleString(undefined, { style: 'currency', currency: o.currency || 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="py-2 pr-4 text-right font-medium cursor-pointer" onClick={() => navigate(`/supplies/${o.id}`)}>
                            {Number(o.grand_total).toLocaleString(undefined, { style: 'currency', currency: o.currency || 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="py-2 pr-2">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/supplies/${o.id}?edit=true`);
                                }}
                                className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                title="Edit"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={(e) => confirmDelete(o, e)}
                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                                title="Delete"
                              >
                                <DeleteIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                        {expandedOrders.has(o.id) && o.lines && o.lines.length > 0 && (
                          <tr className="border-b bg-gray-50">
                            <td colSpan="9" className="py-3 px-4">
                              <div className="text-sm">
                                <div className="font-medium text-gray-700 mb-2">Line Items:</div>
                                <div className="space-y-1">
                                  {o.lines.map((line, idx) => (
                                    <div key={idx} className="flex justify-between items-center py-1 px-2 bg-white rounded border">
                                      <div className="flex-1">
                                        <span className="font-medium">{line.product_name || 'Unnamed Product'}</span>
                                        {line.brand_name && <span className="text-gray-500 ml-2">({line.brand_name})</span>}
                                        {line.category_name && <span className="text-gray-400 ml-1">- {line.category_name}</span>}
                                      </div>
                                      <div className="text-right text-sm">
                                        <div>Qty: {Number(line.qty).toFixed(2)}</div>
                                        <div className="text-gray-600">
                                          {Number(line.unit_price).toLocaleString(undefined, { style: 'currency', currency: o.currency || 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })} each
                                        </div>
                                        <div className="font-medium">
                                          {Number(line.line_total).toLocaleString(undefined, { style: 'currency', currency: o.currency || 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {isModalOpen && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center">
            <div className="absolute inset-0 bg-black opacity-40" onClick={closeModal}></div>
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-full sm:max-w-4xl p-4 sm:p-6 mx-2 sm:mx-0 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base sm:text-lg font-semibold">New Purchase</h3>
                <button onClick={closeModal} className="p-2 rounded hover:bg-gray-100"><X /></button>
              </div>
              {error && (
                <div className="mb-3 p-3 rounded bg-red-50 text-red-700 text-sm">{error}</div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mb-4">
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Vendor</label>
                  <input name="vendorName" value={form.vendorName} onChange={(e)=>{ updateHeader(e); fetchVendors(e.target.value); }} list="vendor-list" className="w-full border rounded px-3 py-2" placeholder="e.g., Tractor Supply" />
                  <datalist id="vendor-list">
                    {vendorOptions.map(v => (
                      <option key={v.id} value={v.name} />
                    ))}
                  </datalist>
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Order Date</label>
                  <input type="date" name="orderDate" value={form.orderDate} onChange={updateHeader} className="w-full border rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Shipping</label>
                  <input name="shipping" value={form.shipping} onChange={updateHeader} className="w-full border rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Other (header)</label>
                  <input name="other_total" value={form.other_total} onChange={updateHeader} className="w-full border rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Tax (header)</label>
                  <input name="tax_total" value={form.tax_total} onChange={updateHeader} className="w-full border rounded px-3 py-2" />
                  <p className="text-xs text-gray-500 mt-1">Additional tax not calculated from line items</p>
                </div>
                <div className="md:col-span-3">
                  <label className="block text-sm text-gray-700 mb-1">Notes</label>
                  <textarea name="notes" value={form.notes} onChange={updateHeader} className="w-full border rounded px-3 py-2" rows={2} />
                </div>
              </div>

              <div className="mb-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <h4 className="font-medium">Line Items</h4>
                <button onClick={addLine} className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded text-sm">Add line</button>
              </div>
              <div className="space-y-4">
                {form.lines.map((ln, idx) => {
                  const qty = Number(ln.qty) || 0;
                  const unitPrice = Number(ln.unit_price) || 0;
                  const taxRate = Number(ln.tax_rate) || 0;
                  const otherAmt = Number(ln.other_line_amt) || 0;
                  const subtotal = qty * unitPrice;
                  const tax = subtotal * taxRate;
                  const lineTotal = subtotal + tax + otherAmt;
                  
                  return (
                    <div key={idx} className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex justify-between items-start mb-3">
                        <h4 className="font-medium text-gray-800">Line Item {idx + 1}</h4>
                        <button onClick={() => removeLine(idx)} className="p-1 text-red-600 hover:bg-red-100 rounded">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      
                      {/* Product, Brand, Category - Full width for text */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Product</label>
                          <input 
                            value={ln.productName} 
                            onChange={(e) => updateLine(idx, 'productName', e.target.value)} 
                            className="w-full border rounded px-3 py-2 text-sm" 
                            placeholder="Product name" 
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Brand</label>
                          <input 
                            value={ln.brandName} 
                            onChange={(e) => { updateLine(idx, 'brandName', e.target.value); fetchBrands(e.target.value); }} 
                            list={`brand-list-${idx}`} 
                            className="w-full border rounded px-3 py-2 text-sm" 
                            placeholder="Brand name" 
                          />
                          <datalist id={`brand-list-${idx}`}>{brandOptions.map(b => (<option key={b.id} value={b.name} />))}</datalist>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Category</label>
                          <input 
                            value={ln.categoryName} 
                            onChange={(e) => { updateLine(idx, 'categoryName', e.target.value); fetchCategories(e.target.value); }} 
                            list={`category-list-${idx}`} 
                            className="w-full border rounded px-3 py-2 text-sm" 
                            placeholder="Category" 
                          />
                          <datalist id={`category-list-${idx}`}>{categoryOptions.map(c => (<option key={c.id} value={c.name} />))}</datalist>
                        </div>
                      </div>

                      {/* Qty, Unit, Price - Smaller fields */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Quantity</label>
                          <input 
                            value={ln.qty} 
                            onChange={(e) => updateLine(idx, 'qty', e.target.value)} 
                            className="w-full border rounded px-3 py-2 text-sm" 
                            type="number"
                            step="1"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Unit</label>
                          <input 
                            value={ln.unit_desc} 
                            onChange={(e) => updateLine(idx, 'unit_desc', e.target.value)} 
                            className="w-full border rounded px-3 py-2 text-sm" 
                            placeholder="Each/Bag" 
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Unit Price</label>
                          <input 
                            value={ln.unit_price} 
                            onChange={(e) => updateLine(idx, 'unit_price', e.target.value)} 
                            className="w-full border rounded px-3 py-2 text-sm" 
                            type="number"
                            step="1"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Tax Rate</label>
                          <input 
                            value={ln.tax_rate} 
                            onChange={(e) => updateLine(idx, 'tax_rate', e.target.value)} 
                            className="w-full border rounded px-3 py-2 text-sm" 
                            placeholder="0.07"
                            type="number"
                            step="0.01"
                          />
                        </div>
                      </div>

                      {/* Other Amount and Notes */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Other Amount</label>
                          <input 
                            value={ln.other_line_amt} 
                            onChange={(e) => updateLine(idx, 'other_line_amt', e.target.value)} 
                            className="w-full border rounded px-3 py-2 text-sm" 
                            type="number"
                            step="0.01"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Notes</label>
                          <input 
                            value={ln.notes} 
                            onChange={(e) => updateLine(idx, 'notes', e.target.value)} 
                            className="w-full border rounded px-3 py-2 text-sm" 
                            placeholder="Optional notes" 
                          />
                        </div>
                      </div>

                      {/* Line Totals */}
                      <div className="border-t pt-3">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          <div>
                            <span className="text-gray-600">Subtotal:</span>
                            <div className="font-medium">${subtotal.toFixed(2)}</div>
                          </div>
                          <div>
                            <span className="text-gray-600">Tax:</span>
                            <div className="font-medium">${tax.toFixed(2)}</div>
                          </div>
                          <div>
                            <span className="text-gray-600">Other:</span>
                            <div className="font-medium">${otherAmt.toFixed(2)}</div>
                          </div>
                          <div>
                            <span className="text-gray-600">Line Total:</span>
                            <div className="font-bold text-blue-600">${lineTotal.toFixed(2)}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Grand Total */}
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                {(() => {
                  // Calculate line items totals
                  const lineItemsTotal = form.lines.reduce((total, ln) => {
                    const qty = Number(ln.qty) || 0;
                    const unitPrice = Number(ln.unit_price) || 0;
                    const taxRate = Number(ln.tax_rate) || 0;
                    const otherAmt = Number(ln.other_line_amt) || 0;
                    const subtotal = qty * unitPrice;
                    const tax = subtotal * taxRate;
                    return total + subtotal + tax + otherAmt;
                  }, 0);
                  
                  // Get header values
                  const shipping = Number(form.shipping) || 0;
                  const otherTotal = Number(form.other_total) || 0;
                  const taxTotal = Number(form.tax_total) || 0;
                  
                  // Calculate grand total
                  const grandTotal = lineItemsTotal + shipping + otherTotal + taxTotal;
                  
                  return (
                    <>
                      {/* Breakdown */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
                        <div>
                          <span className="text-gray-600">Line Items:</span>
                          <div className="font-medium">${lineItemsTotal.toFixed(2)}</div>
                        </div>
                        <div>
                          <span className="text-gray-600">Shipping:</span>
                          <div className="font-medium">${shipping.toFixed(2)}</div>
                        </div>
                        <div>
                          <span className="text-gray-600">Other (Header):</span>
                          <div className="font-medium">${otherTotal.toFixed(2)}</div>
                        </div>
                        <div>
                          <span className="text-gray-600">Tax (Header):</span>
                          <div className="font-medium">${taxTotal.toFixed(2)}</div>
                        </div>
                      </div>
                      
                      {/* Grand Total */}
                      <div className="flex justify-between items-center border-t pt-3">
                        <span className="text-lg font-semibold text-gray-800">Grand Total:</span>
                        <span className="text-2xl font-bold text-blue-600">${grandTotal.toFixed(2)}</span>
                      </div>
                    </>
                  );
                })()}
              </div>

              <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2">
                <button onClick={closeModal} className="px-4 py-2 rounded border w-full sm:w-auto">Cancel</button>
                <button onClick={saveOrder} disabled={saving} className={`px-4 py-2 rounded text-white w-full sm:w-auto ${saving ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}`}>{saving ? 'Saving...' : 'Save Purchase'}</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && deletingOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <AlertTriangle className="w-6 h-6 text-red-600 mr-3" />
              <h3 className="text-lg font-semibold">Confirm Delete</h3>
            </div>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete the purchase from <span className="font-semibold">{deletingOrder.vendor_name}</span> on <span className="font-semibold">{formatDate(deletingOrder.order_date)}</span>? This action cannot be undone and will remove all associated line items.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeletingOrder(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={deleteOrder}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


